import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import get from 'lodash/get';
import { resolveDiagramTokens } from '@kentik/ui-shared/kaizen/diagramTokens';
import { sanitizeSvg } from 'core/util/sanitizeSvg';
import Flex from './Flex';
import Text from './Text';
import Callout from './Callout';
import Dialog from './Dialog';
import Button from './Button';
import Tooltip from './Tooltip';
import Popover from './Popover';
import Menu from './Menu';
import MenuItem from './MenuItem';

// The design system for generated SVG: `--kd-*` custom properties the diagram references as
// `fill="var(--kd-surface, transparent)"`, plus `color` backing `currentColor`. Declared on the
// wrapper rather than inside the SVG so the same markup re-themes on light/dark toggle.
const diagramTheme = css`
  ${({ theme }) =>
    resolveDiagramTokens(theme, get)
      .map(({ name, value }) => `${name}: ${value};`)
      .join('\n')}
  color: ${({ theme }) => theme.colors.body};
  font-family: ${({ theme }) => theme.fonts.body};
`;

let nextDiagramScope = 0;

const SvgContainer = styled.div`
  position: relative;

  .svg-diagram {
    ${diagramTheme};
    display: flex;
    justify-content: center;
    margin: 8px 0;

    svg {
      width: 100%;
      max-width: 100%;
      height: auto;
    }
  }
`;

const ZoomButton = styled(Button)`
  margin-right: 8px;
  z-index: 1;
`;

const DialogContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;

  .svg-diagram {
    ${diagramTheme};
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;

    svg {
      width: 100%;
      max-width: 100%;
      max-height: 100%;
      height: auto;
    }
  }
`;

/**
 * SvgDiagram - Safely renders an untrusted SVG string (e.g. AI-generated SVG).
 *
 * The SVG is sanitized with DOMPurify before being inserted into the DOM, stripping any
 * embedded scripts, event handlers and `<foreignObject>` HTML to prevent XSS.
 *
 * @param {string} svg - The raw SVG markup to render.
 */
const SvgDiagram = ({ svg }) => {
  const [isZoomDialogOpen, setIsZoomDialogOpen] = useState(false);
  const [diagramScope] = useState(() => {
    const scope = `svg-diagram-${nextDiagramScope}`;
    nextDiagramScope += 1;
    return scope;
  });

  const sanitizedSvg = useMemo(() => sanitizeSvg(svg, diagramScope), [diagramScope, svg]);
  const zoomedSanitizedSvg = useMemo(() => sanitizeSvg(svg, `${diagramScope}-zoom`), [diagramScope, svg]);

  const handleSaveAsSVG = () => {
    if (!sanitizedSvg) {
      return;
    }

    const blob = new Blob([sanitizedSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `diagram-${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!sanitizedSvg) {
    return (
      <Callout intent="danger" title="Unable to render SVG">
        <Text small>The provided SVG content was empty or could not be safely displayed.</Text>
      </Callout>
    );
  }

  const renderExportMenu = () => (
    <Menu>
      <MenuItem text="Save as SVG" label=".svg" onClick={handleSaveAsSVG} />
    </Menu>
  );

  /* eslint-disable react/no-danger */
  return (
    <>
      <SvgContainer>
        <Flex justifyContent="flex-end" mb={1}>
          <Tooltip content="Zoom" position="top">
            <ZoomButton icon="zoom-in" small onClick={() => setIsZoomDialogOpen(true)} title="Zoom" />
          </Tooltip>
          <Popover position="bottom-right">
            <Tooltip content="Export" position="top">
              <Button icon="export" small title="Export" />
            </Tooltip>
            {renderExportMenu()}
          </Popover>
        </Flex>
        <div className="svg-diagram" dangerouslySetInnerHTML={{ __html: sanitizedSvg }} />
      </SvgContainer>

      <Dialog
        isOpen={isZoomDialogOpen}
        onClose={() => setIsZoomDialogOpen(false)}
        title="SVG Diagram"
        style={{ width: '90vw', height: '90vh' }}
      >
        <DialogContent>
          <div className="svg-diagram" dangerouslySetInnerHTML={{ __html: zoomedSanitizedSvg }} />
        </DialogContent>
        <Dialog.Footer>
          <Flex justifyContent="flex-end" width="100%" gap={1}>
            <Popover position="top">
              <Button icon="export" text="Save" />
              {renderExportMenu()}
            </Popover>
            <Button onClick={() => setIsZoomDialogOpen(false)} text="Close" />
          </Flex>
        </Dialog.Footer>
      </Dialog>
    </>
  );
  /* eslint-enable react/no-danger */
};

export default SvgDiagram;
